import { Component, OnInit } from '@angular/core';
import { ConfirmDialogService } from '../../confirm-dialog/confirm-dialog.service';
import { NotificationService } from '../../notification/notification.service';
import {
  ComponentLevelRow,
  DispatchClass,
  DispatchClassEtsyProfile,
  EtsyShippingProfile,
  WarehouseRuleConfig,
  WarehouseRulePreviewRow,
  WarehouseRulesService,
  WarehouseStockState,
} from '../api';

type TierKey = 'ok' | 'min' | 'out';

interface TierRow {
  key: TierKey;
  chipLabel: string;
  desc: string;
  tone: 'green' | 'yellow' | 'red';
}

@Component({
  selector: 'app-raktar-szabaly',
  templateUrl: './raktar-szabaly.component.html',
  styleUrl: './raktar-szabaly.component.scss'
})
export class RaktarSzabalyComponent implements OnInit {

  readonly lookbackOptions: number[] = [1, 2, 3, 4, 6];
  // 150% (1.5) is a szezonális csúcsokra (pl. karácsony előtti előgyártás).
  readonly factorOptions: number[] = [0.25, 0.5, 0.75, 1, 1.5];

  readonly tierRows: TierRow[] = [
    { key: 'ok',  chipLabel: 'Készleten',       desc: 'Bőven a számolt alapszint felett — minden rendben.',                     tone: 'green' },
    { key: 'min', chipLabel: 'Minimum szinten', desc: 'Elérte vagy alatta van a számolt alapszintnek.',                          tone: 'yellow' },
    { key: 'out', chipLabel: 'Elfogyott',       desc: 'Nincs összeállítható darab (0) — visszaáll, ha újra készleten van.',      tone: 'red' },
  ];

  config: WarehouseRuleConfig = {
    lookbackMonths: 3,
    factor: 0.5,
    autoAssignEnabled: true,
    ruleEnabled: true,
    etsySyncEnabled: false,
    lastAppliedAt: null,
    dispatchClasses: [],
    tierOkClassId: 0,
    tierMinClassId: 0,
    tierOutClassId: 0,
  };

  // --- Komponens szintek (4. kártya) ---
  componentRows: ComponentLevelRow[] = [];
  componentsLoading = false;
  componentSearch = '';

  // --- Etsy szállítási profilok (2. kártya legördülője) ---
  etsyProfiles: EtsyShippingProfile[] = [];
  etsyProfilesLoading = false;
  etsyProfilesError = '';

  recalculating = false;
  syncingEtsy = false;

  previewRows: WarehouseRulePreviewRow[] = [];
  previewLoading = false;
  previewUnavailable = false;

  private saveErrorNotified = false;
  private readonly gameBadgePalette = ['#9A6E9E', '#5E7FA8', '#8FB859', '#C9A45C', '#C97C7C', '#7A8DA0'];

  constructor(
    private warehouseRules: WarehouseRulesService,
    private notificationService: NotificationService,
    private confirmDialog: ConfirmDialogService,
  ) {}

  ngOnInit(): void {
    // Az előnézetet CSAK a konfig megérkezése után kérjük le — különben a
    // szerver-oldali kalkuláció a helyi alapértékekkel (2 hónap / 50%) futna,
    // miközben a chipek a mentett beállítást mutatják.
    this.fetchConfig();
    this.fetchComponentLevels();
  }

  // ---------------------------------------------------------------------
  // Adatbetöltés
  // ---------------------------------------------------------------------

  fetchConfig() {
    this.warehouseRules.getConfig().subscribe({
      next: (config) => {
        if (config) {
          this.config = { ...this.config, ...config, dispatchClasses: config.dispatchClasses || [] };
        }
        this.fetchPreview();
        // A profilneveket akkor is lekérjük, ha a szinkron ki van kapcsolva, de
        // már van hozzárendelt profil — különben a legördülőben csak a
        // számazonosító látszana.
        if (this.config.etsySyncEnabled || this.hasAnyEtsyProfile) {
          this.fetchEtsyProfiles();
        }
      },
      error: () => {
        // Backend még nem él — a képernyő a helyi alapértékekkel működik tovább.
        this.fetchPreview();
      },
    });
  }

  fetchComponentLevels() {
    this.componentsLoading = true;
    this.warehouseRules.getComponentLevels().subscribe({
      next: (rows) => {
        this.componentRows = rows || [];
        this.componentsLoading = false;
      },
      error: () => {
        this.componentRows = [];
        this.componentsLoading = false;
      },
    });
  }

  get visibleComponentRows(): ComponentLevelRow[] {
    const needle = (this.componentSearch || '').trim().toLowerCase();
    if (!needle) {
      return this.componentRows;
    }
    return this.componentRows.filter((row) =>
      (row.name || '').toLowerCase().includes(needle)
      || (row.sku || '').toLowerCase().includes(needle));
  }

  fetchEtsyProfiles() {
    if (this.etsyProfilesLoading) {
      return;
    }
    this.etsyProfilesLoading = true;
    this.etsyProfilesError = '';
    this.warehouseRules.getEtsyShippingProfiles().subscribe({
      next: (profiles) => {
        this.etsyProfiles = profiles || [];
        this.etsyProfilesLoading = false;
        if (!this.etsyProfiles.length) {
          this.etsyProfilesError = 'Nem érkezett szállítási profil az Etsy-ről — ellenőrizd, hogy a bolt be van-e kötve.';
        }
      },
      error: (error) => {
        this.etsyProfiles = [];
        this.etsyProfilesLoading = false;
        this.etsyProfilesError = error?.error?.message
          || 'Az Etsy szállítási profilok lekérdezése nem sikerült.';
      },
    });
  }

  profileLabel(profile: EtsyShippingProfile): string {
    const days = profile.minProcessingDays === null && profile.maxProcessingDays === null
      ? ''
      : ` · ${profile.minProcessingDays ?? '?'}–${profile.maxProcessingDays ?? '?'} nap`;
    // A bolt neve a sor elején már látszik, ezért itt csak a profil kell.
    return `${profile.title}${days}`;
  }

  fetchPreview() {
    this.previewLoading = true;
    this.warehouseRules.getPreview(this.config.lookbackMonths, this.config.factor).subscribe({
      next: (rows) => {
        this.previewRows = rows || [];
        this.previewUnavailable = false;
        this.previewLoading = false;
      },
      error: () => {
        this.previewRows = [];
        this.previewUnavailable = true;
        this.previewLoading = false;
      },
    });
  }

  private persistConfig(patch: Partial<Omit<WarehouseRuleConfig, 'dispatchClasses'>>) {
    this.warehouseRules.updateConfig(patch).subscribe({
      next: (config) => {
        if (config) {
          this.config = { ...this.config, ...config, dispatchClasses: config.dispatchClasses || this.config.dispatchClasses };
        }
      },
      error: () => this.notifySaveErrorOnce(),
    });
  }

  private notifySaveErrorOnce() {
    if (this.saveErrorNotified) {
      return;
    }
    this.saveErrorNotified = true;
    this.notificationService.show('A raktár szabály mentése nem sikerült — a beállítások csak helyben élnek.', 'warning');
  }

  // ---------------------------------------------------------------------
  // 1 · Alapszint kalkuláció
  // ---------------------------------------------------------------------

  setLookback(months: number) {
    if (this.config.lookbackMonths === months) {
      return;
    }
    this.config = { ...this.config, lookbackMonths: months };
    this.persistConfig({ lookbackMonths: months });
    this.fetchPreview();
  }

  setFactor(factor: number) {
    if (Math.abs(this.config.factor - factor) < 0.001) {
      return;
    }
    // Kikapcsolt szabálynál a % csak beállítás (semmit nem ír át) — nem kell
    // megerősítés.
    if (!this.config.ruleEnabled) {
      this.config = { ...this.config, factor };
      this.persistConfig({ factor });
      this.fetchPreview();
      return;
    }
    // A szorzó módosítása minden termék minimum készletszintjét (alapszintjét)
    // újraszámolja — megerősítést kérünk, mielőtt életbe lép.
    const currentPercent = Math.round(this.config.factor * 100);
    const newPercent = Math.round(factor * 100);
    this.confirmDialog.open({
      title: 'Alapszint átállítása?',
      message: `A szorzót ${currentPercent}%-ról ${newPercent}%-ra állítod — ezzel minden termék `
        + `minimum készletszintje (alapszintje) újraszámolódik.`,
      tone: 'warning',
      confirmLabel: 'Átállítom',
      cancelLabel: 'Mégse',
    }).subscribe((result) => {
      if (!result?.confirmed) {
        return;
      }
      this.config = { ...this.config, factor };
      this.persistConfig({ factor });
      this.fetchPreview();
    });
  }

  isFactorActive(factor: number): boolean {
    return Math.abs(this.config.factor - factor) < 0.001;
  }

  factorLabel(factor: number): string {
    return Math.round(factor * 100) + '%';
  }

  get exampleText(): string {
    const example = this.previewRows[0];
    if (!example) {
      return '';
    }
    const avg = Math.round((example.avgMonthlySales || 0) * 10) / 10;
    const percent = Math.round(this.config.factor * 100);
    const computed = Math.round(avg * this.config.factor);
    const intro = 'Példa: a(z) „' + example.name + '” termékből az elmúlt ' + this.config.lookbackMonths
      + ' hónapban átlag ' + avg + ' db fogyott havonta — az alapszint ' + avg + ' × ' + percent + '%';
    // A szerver az alapszintet legalább 1 db-ban állapítja meg
    // (Math.max(1, ...)), ezért ritkán fogyó terméknél a puszta képlet
    // hazudna: „0 × 50% = 1 db”. Ilyenkor kiírjuk a lekerekítés okát is.
    if (example.minLevel > computed) {
      return intro + ' = ' + computed + ' db, de az alapszint legalább 1 db — így '
        + example.minLevel + ' db lesz.';
    }
    return intro + ' = ' + example.minLevel + ' db lesz.';
  }

  // ---------------------------------------------------------------------
  // 2 · Feladási osztályok
  // ---------------------------------------------------------------------

  daysLabel(dispatchClass: DispatchClass): string {
    if (!dispatchClass) {
      return '';
    }
    return dispatchClass.minDays === dispatchClass.maxDays
      ? dispatchClass.minDays + ' nap'
      : dispatchClass.minDays + '–' + dispatchClass.maxDays + ' nap';
  }

  onClassNameChange(dispatchClass: DispatchClass, event: Event) {
    const name = (event.target as HTMLInputElement).value;
    dispatchClass.name = name;
    this.warehouseRules.updateDispatchClass(dispatchClass.id, { name }).subscribe({
      next: () => {},
      error: () => this.notifySaveErrorOnce(),
    });
  }

  onClassMinChange(dispatchClass: DispatchClass, event: Event) {
    const minDays = this.parseIntValue(event);
    dispatchClass.minDays = minDays;
    this.warehouseRules.updateDispatchClass(dispatchClass.id, { minDays }).subscribe({
      // A backend érvényesíti a tartományt (max ≥ min) — a válaszból frissítünk,
      // hogy a korrigált értékek a felületen is megjelenjenek.
      next: (updated) => {
        dispatchClass.minDays = updated.minDays;
        dispatchClass.maxDays = updated.maxDays;
      },
      error: () => this.notifySaveErrorOnce(),
    });
  }

  onClassMaxChange(dispatchClass: DispatchClass, event: Event) {
    const maxDays = this.parseIntValue(event);
    dispatchClass.maxDays = maxDays;
    this.warehouseRules.updateDispatchClass(dispatchClass.id, { maxDays }).subscribe({
      next: (updated) => {
        dispatchClass.minDays = updated.minDays;
        dispatchClass.maxDays = updated.maxDays;
      },
      error: () => this.notifySaveErrorOnce(),
    });
  }

  private parseIntValue(event: Event): number {
    const raw = ((event.target as HTMLInputElement).value || '').replace(/[^0-9]/g, '');
    const value = parseInt(raw, 10);
    return isNaN(value) ? 0 : value;
  }

  addDispatchClass() {
    this.warehouseRules.createDispatchClass({ name: 'Új osztály', minDays: 1, maxDays: 3 }).subscribe({
      next: (created) => {
        this.config = { ...this.config, dispatchClasses: [...this.config.dispatchClasses, created] };
      },
      error: () => this.notifySaveErrorOnce(),
    });
  }

  removeDispatchClass(dispatchClass: DispatchClass) {
    // A törlés végleges és azonnal érvényes a feladási szabályra — a többi
    // képernyőhöz igazodva megerősítést kérünk (korábban egy kattintás
    // visszavonhatatlanul törölte az osztályt).
    this.confirmDialog.open({
      title: 'Biztosan törlöd?',
      message: 'A(z) „' + dispatchClass.name + '” feladási osztály törlődik.',
      tone: 'danger',
      confirmLabel: 'Törlés',
      cancelLabel: 'Mégse',
    }).subscribe((result) => {
      if (!result?.confirmed) {
        return;
      }
      this.warehouseRules.deleteDispatchClass(dispatchClass.id).subscribe({
        next: () => {
          this.config = {
            ...this.config,
            dispatchClasses: this.config.dispatchClasses.filter((c) => c.id !== dispatchClass.id),
          };
          this.notificationService.show('A feladási osztály törölve.', 'success');
        },
        error: (error) => {
          if (error?.status === 409) {
            this.notificationService.show('Nem törölhető: az automatikus feladási szabály hivatkozik erre az osztályra.', 'warning');
            return;
          }
          this.notifySaveErrorOnce();
        },
      });
    });
  }

  trackDispatchClass(_index: number, dispatchClass: DispatchClass) {
    return dispatchClass.id;
  }

  // ---------------------------------------------------------------------
  // 3 · Automatikus feladási szabály
  // ---------------------------------------------------------------------

  toggleAuto() {
    const autoAssignEnabled = !this.config.autoAssignEnabled;
    this.config = { ...this.config, autoAssignEnabled };
    this.persistConfig({ autoAssignEnabled });
  }

  /** Etsy szinkron be/ki: a feladási osztály a lista szállítási profilját váltja. */
  toggleEtsySync() {
    const etsySyncEnabled = !this.config.etsySyncEnabled;
    if (etsySyncEnabled) {
      this.confirmDialog.open({
        title: 'Etsy szinkron bekapcsolása?',
        message: 'Bekapcsolva a rendszer az Etsy-n is átállítja az érintett listák szállítási profilját '
          + '(és ezzel a feladási időt), amikor egy termék fogyóban van vagy elfogyott.',
        details: 'Előbb minden feladási osztályhoz rendelj Etsy szállítási profilt — profil nélküli osztályt a szinkron kihagy.',
        tone: 'warning',
        confirmLabel: 'Bekapcsolom',
        cancelLabel: 'Mégse',
      }).subscribe((result) => {
        if (!result?.confirmed) {
          return;
        }
        this.config = { ...this.config, etsySyncEnabled };
        this.persistConfig({ etsySyncEnabled });
        this.fetchEtsyProfiles();
      });
      return;
    }
    this.config = { ...this.config, etsySyncEnabled };
    this.persistConfig({ etsySyncEnabled });
  }

  get hasAnyEtsyProfile(): boolean {
    return this.config.dispatchClasses.some((dispatchClass) =>
      (dispatchClass.etsyProfiles || []).some((profile) => !!profile.shippingProfileId));
  }

  /** Egy bolt szállítási profiljai — csak azok, amik ahhoz a bolthoz tartoznak. */
  profilesForShop(shopId: number): EtsyShippingProfile[] {
    return this.etsyProfiles.filter((profile) => profile.shopId === shopId);
  }

  onClassProfileChange(
    dispatchClass: DispatchClass,
    assignment: DispatchClassEtsyProfile,
    event: Event,
  ) {
    const shippingProfileId = (event.target as HTMLSelectElement).value || null;
    assignment.shippingProfileId = shippingProfileId;
    this.warehouseRules
      .setDispatchClassEtsyProfile(dispatchClass.id, assignment.shopId, shippingProfileId)
      .subscribe({
        next: () => {},
        error: () => this.notifySaveErrorOnce(),
      });
  }

  /** Teljes újraszámolás kézzel — a napi automatikus futás mellé. */
  recalculateNow() {
    if (this.recalculating) {
      return;
    }
    this.recalculating = true;
    this.warehouseRules.recalculate().subscribe({
      next: (result) => {
        this.recalculating = false;
        this.notificationService.show(
          `Újraszámolva: ${result.products} termék, ${result.components} komponens · `
          + `${result.limitChanges} minimum és ${result.dispatchChanges} feladási osztály módosult.`,
          'success',
        );
        this.fetchConfig();
        this.fetchComponentLevels();
      },
      error: (error) => {
        this.recalculating = false;
        this.notificationService.show(
          `Az újraszámolás nem sikerült! Részletek: ${error?.error?.message || error?.message}`,
          'danger',
        );
      },
    });
  }

  /** A feladási osztályok kiküldése az Etsy-re, kézzel. */
  syncEtsyNow() {
    if (this.syncingEtsy) {
      return;
    }
    this.syncingEtsy = true;
    this.warehouseRules.syncEtsy().subscribe({
      next: (result) => {
        this.syncingEtsy = false;
        if (result.errors?.length) {
          this.notificationService.show(
            `${result.updated} lista frissítve, ${result.failed} sikertelen. ${result.errors[0]}`,
            result.updated ? 'warning' : 'danger',
          );
          return;
        }
        this.notificationService.show(
          result.updated
            ? `${result.updated} Etsy lista feladási ideje frissítve.`
            : 'Minden Etsy lista már a megfelelő profilon van.',
          'success',
        );
      },
      error: (error) => {
        this.syncingEtsy = false;
        this.notificationService.show(
          `Az Etsy szinkron nem sikerült! Részletek: ${error?.error?.message || error?.message}`,
          'danger',
        );
      },
    });
  }

  // ---------------------------------------------------------------------
  // 5 · Komponens szintek
  // ---------------------------------------------------------------------

  sourceLabel(source: 'auto' | 'manual'): string {
    return source === 'auto' ? 'Automatikus' : 'Kézi';
  }

  toggleComponentExempt(row: ComponentLevelRow) {
    const exempt = !row.dispatchRuleExempt;
    row.dispatchRuleExempt = exempt;
    this.warehouseRules.setDispatchExempt(row.componentId, exempt).subscribe({
      next: () => {},
      error: () => {
        row.dispatchRuleExempt = !exempt;
        this.notificationService.show('A kapcsoló mentése nem sikerült.', 'danger');
      },
    });
  }

  onComponentManualLimitChange(row: ComponentLevelRow, event: Event) {
    const raw = ((event.target as HTMLInputElement).value || '').replace(/[^0-9]/g, '');
    const manualLimit = parseInt(raw, 10);
    if (isNaN(manualLimit)) {
      (event.target as HTMLInputElement).value = `${row.manualLevel}`;
      return;
    }
    this.warehouseRules.setManualLimit(row.componentId, manualLimit).subscribe({
      next: (saved) => {
        row.manualLevel = saved.manualLimit;
        row.effectiveLevel = saved.effectiveLimit;
        row.source = saved.source;
      },
      error: () => {
        (event.target as HTMLInputElement).value = `${row.manualLevel}`;
        this.notificationService.show('A kézi minimum mentése nem sikerült.', 'danger');
      },
    });
  }

  trackComponentRow(_index: number, row: ComponentLevelRow) {
    return row.componentId;
  }

  /** Alapszint-szabály be/ki. Bekapcsoláskor a szerver azonnal újraszámolja
   *  és ráírja a minimumokat, ezért megerősítést kérünk. */
  toggleRule() {
    const ruleEnabled = !this.config.ruleEnabled;
    if (ruleEnabled) {
      this.confirmDialog.open({
        title: 'Raktár szabály bekapcsolása?',
        message: 'Bekapcsoláskor a rendszer az eladási átlagokból azonnal újraszámolja és átírja a termékek minimum készletszintjét. (Az egyedi és az eladási előzmény nélküli termékekhez nem nyúl.)',
        tone: 'warning',
        confirmLabel: 'Bekapcsolom',
        cancelLabel: 'Mégse',
      }).subscribe((result) => {
        if (!result?.confirmed) {
          return;
        }
        this.config = { ...this.config, ruleEnabled };
        this.persistConfig({ ruleEnabled });
        this.fetchPreview();
      });
      return;
    }
    this.config = { ...this.config, ruleEnabled };
    this.persistConfig({ ruleEnabled });
  }

  tierClassId(key: TierKey): number {
    if (key === 'ok') return this.config.tierOkClassId;
    if (key === 'min') return this.config.tierMinClassId;
    return this.config.tierOutClassId;
  }

  onTierChange(key: TierKey, event: Event) {
    const id = parseInt((event.target as HTMLSelectElement).value, 10);
    if (isNaN(id)) {
      return;
    }
    if (key === 'ok') {
      this.config = { ...this.config, tierOkClassId: id };
      this.persistConfig({ tierOkClassId: id });
    } else if (key === 'min') {
      this.config = { ...this.config, tierMinClassId: id };
      this.persistConfig({ tierMinClassId: id });
    } else {
      this.config = { ...this.config, tierOutClassId: id };
      this.persistConfig({ tierOutClassId: id });
    }
  }

  classOptionLabel(dispatchClass: DispatchClass): string {
    return dispatchClass.name + ' (' + this.daysLabel(dispatchClass) + ')';
  }

  // ---------------------------------------------------------------------
  // 4 · Élő előnézet
  // ---------------------------------------------------------------------

  stateLabel(state: WarehouseStockState): string {
    if (state === 'out') return 'Elfogyott';
    if (state === 'min') return 'Minimum szinten';
    return 'Készleten';
  }

  activeClassId(row: WarehouseRulePreviewRow): number {
    if (!this.config.autoAssignEnabled) {
      return this.config.tierOkClassId;
    }
    if (row.state === 'out') return this.config.tierOutClassId;
    if (row.state === 'min') return this.config.tierMinClassId;
    return this.config.tierOkClassId;
  }

  activeClassName(row: WarehouseRulePreviewRow): string {
    const found = this.config.dispatchClasses.find((c) => c.id === this.activeClassId(row));
    if (found) {
      return found.name;
    }
    return this.config.dispatchClasses.length ? this.config.dispatchClasses[0].name : '—';
  }

  activeClassDays(row: WarehouseRulePreviewRow): string {
    const found = this.config.dispatchClasses.find((c) => c.id === this.activeClassId(row))
      || this.config.dispatchClasses[0];
    return found ? this.daysLabel(found) : '';
  }

  avgLabel(row: WarehouseRulePreviewRow): number {
    return Math.round((row.avgMonthlySales || 0) * 10) / 10;
  }

  shopBadgeColor(shop: string | null): string {
    if (!shop) {
      return '#9AA098';
    }
    let hash = 0;
    for (let i = 0; i < shop.length; i++) {
      hash = (hash * 31 + shop.charCodeAt(i)) >>> 0;
    }
    return this.gameBadgePalette[hash % this.gameBadgePalette.length];
  }

  trackPreviewRow(_index: number, row: WarehouseRulePreviewRow) {
    return row.sku;
  }
}
